import moment, { type Moment } from 'moment'
import { toLocalDatetimeInputValue } from '../../../../utils/commonUtils'
import { muahangKiotLike } from '../../../layout/muahangKiotTokens'

export const DATE_ISO = 'YYYY-MM-DD'
export const HOURS_24 = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'))
export const MINUTES_60 = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'))
export const WEEKDAYS_VI = ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'] as const

const buildTimeSlots = (stepMinutes: number): string[] => {
  const count = Math.floor((24 * 60) / stepMinutes)
  return Array.from({ length: count }, (_, i) => {
    const total = i * stepMinutes
    const hh = String(Math.floor(total / 60)).padStart(2, '0')
    const mm = String(total % 60).padStart(2, '0')
    return `${hh}:${mm}`
  })
}

/** Slot giờ 24h, bước 15 phút: `00:00` … `23:45`. */
export const TIME_SLOTS_15 = buildTimeSlots(15)

/** Slot giờ 24h, bước 30 phút: `00:00` … `23:30` (giữ tương thích). */
export const TIME_SLOTS_30 = buildTimeSlots(30)

/** Bước mặc định của time picker dropdown. */
export const TIME_STEP_MINUTES = 15

/** Alias mặc định = 15 phút. */
export const TIME_SLOTS = TIME_SLOTS_15

const snapTimeToStep = (raw: string, stepMinutes: number): string => {
  const t = raw.trim()
  const m = t.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return '00:00'
  let h = Number(m[1])
  let min = Number(m[2])
  if (h > 23) h = 23
  if (h < 0) h = 0
  if (min > 59) min = 59
  if (min < 0) min = 0
  const total = h * 60 + min
  const max = 24 * 60 - stepMinutes
  const snapped = Math.min(max, Math.round(total / stepMinutes) * stepMinutes)
  const hh = String(Math.floor(snapped / 60)).padStart(2, '0')
  const mm = String(snapped % 60).padStart(2, '0')
  return `${hh}:${mm}`
}

/** Làm tròn `HH:mm` về slot 15 phút gần nhất. */
export const snapTimeToStep15 = (raw: string): string => snapTimeToStep(raw, 15)

/** Làm tròn `HH:mm` về slot 30 phút gần nhất. */
export const snapTimeToStep30 = (raw: string): string => snapTimeToStep(raw, 30)

export const dateTimePopoverPaperSx = {
  mt: 0.75,
  borderRadius: '10px',
  border: `1px solid ${muahangKiotLike.filterPanelBorder}`,
  boxShadow: '0 10px 28px rgba(15, 23, 42, 0.14)',
  overflow: 'hidden',
  fontFamily: muahangKiotLike.kiotFontStack,
} as const

export const splitLocalDatetime = (value: string) => {
  const v = value?.trim() ?? ''
  if (v.length >= 16) {
    return { date: v.slice(0, 10), time: v.slice(11, 16) }
  }
  if (v.length >= 10) {
    return { date: v.slice(0, 10), time: '' }
  }
  return { date: '', time: '' }
}

/** `YYYY-MM-DD` → `dd/MM/yyyy` */
export const formatDateDisplay = (isoDate: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return ''
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}

export const todayIsoDate = () => toLocalDatetimeInputValue(new Date()).slice(0, 10)

export const parseIsoDate = (v: string): Moment | null => {
  if (!v?.trim()) return null
  const m = moment(v, DATE_ISO, true)
  return m.isValid() ? m.startOf('day') : null
}

/** Ghép `YYYY-MM-DD` + `HH:mm` → `YYYY-MM-DDTHH:mm`. */
export const joinLocalDatetime = (date: string, time: string) => {
  if (!date) return ''
  const t = time.trim() || '00:00'
  return `${date}T${t}`
}

/**
 * Chuẩn hoá chuỗi người dùng gõ thành `dd/MM/yyyy` (đang nhập).
 * Gõ liên tục chỉ số → tự tách `/` (vd: 24092026 → 24/09/2026).
 */
export const maskDateDraft = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 8)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

/**
 * Parse `dd/MM/yyyy` | `ddMMyyyy` | ISO → `YYYY-MM-DD`. Không hợp lệ → `''`.
 */
export const parseDateInputToIso = (raw: string): string => {
  const t = raw.trim()
  if (!t) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    return parseIsoDate(t) ? t : ''
  }
  const strict = moment(t, ['DD/MM/YYYY', 'D/M/YYYY', 'DDMMYYYY'], true)
  if (strict.isValid()) return strict.format(DATE_ISO)
  const digits = t.replace(/\D/g, '')
  if (digits.length === 8) {
    const m = moment(digits, 'DDMMYYYY', true)
    if (m.isValid()) return m.format(DATE_ISO)
  }
  return ''
}

/**
 * Chuẩn hoá chuỗi giờ đang gõ → tối đa `HH:mm` (24h).
 * Gõ liên tục chỉ số → tự tách `:` (vd: 1430 → 14:30).
 */
export const maskTimeDraft = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 4)
  if (digits.length <= 2) return digits
  return `${digits.slice(0, 2)}:${digits.slice(2)}`
}

/** `YYYY-MM-DD` + `HH:mm` → `dd/MM/yyyy HH:mm` (hiển thị 1 ô). */
export const formatDateTimeDisplay = (isoDate: string, hhmm: string): string => {
  const d = formatDateDisplay(isoDate)
  const t = normalizeTimePart(hhmm)
  if (d && t) return `${d} ${t}`
  if (d) return d
  if (t) return t
  return ''
}

const normalizeTimePart = (hhmm: string): string => {
  const t = hhmm.trim()
  if (!t) return ''
  const m = t.match(/^(\d{1,2}):(\d{2})$/)
  if (!m) return ''
  const h = Number(m[1])
  const min = Number(m[2])
  if (h < 0 || h > 23 || min < 0 || min > 59) return ''
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`
}

/**
 * Gõ liên tục ngày+giờ → `dd/MM/yyyy HH:mm`.
 * Vd: 240920261430 → 24/09/2026 14:30
 */
export const maskDateTimeDraft = (raw: string): string => {
  const digits = raw.replace(/\D/g, '').slice(0, 12)
  if (digits.length <= 2) return digits
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
  if (digits.length <= 8) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
  }
  const datePart = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`
  const timeDigits = digits.slice(8)
  if (timeDigits.length <= 2) return `${datePart} ${timeDigits}`
  return `${datePart} ${timeDigits.slice(0, 2)}:${timeDigits.slice(2)}`
}

/** Tách draft `dd/MM/yyyy HH:mm` → `{ dateIso, time }`. */
export const parseDateTimeDraft = (
  raw: string,
): { dateIso: string; time: string } => {
  const digits = raw.replace(/\D/g, '')
  const dateIso =
    digits.length >= 8 ? parseDateInputToIso(digits.slice(0, 8)) : parseDateInputToIso(raw)
  let time = ''
  if (digits.length >= 12) {
    time = normalizeTimePart(`${digits.slice(8, 10)}:${digits.slice(10, 12)}`)
  } else if (digits.length > 8) {
    time = maskTimeDraft(digits.slice(8))
    if (time.length === 5) time = normalizeTimePart(time)
    else time = ''
  } else {
    const spaced = raw.trim().split(/\s+/)
    if (spaced.length >= 2) {
      time = normalizeTimePart(maskTimeDraft(spaced[1])) || normalizeTimePart(spaced[1])
    }
  }
  return { dateIso, time }
}
